<template>
  <el-dialog
    :visible.sync="visible"
    :title="title"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="finished || errored || step === 'select'"
    width="720px">

    <div v-if="step === 'select'">
      <div v-if="loadingColumns" class="text-center" style="padding: 20px 0;">
        <i class="el-icon-loading" style="font-size: 32px; color: #409eff;"></i>
        <p class="mt-3" style="margin-top: 12px;">Chargement des colonnes…</p>
      </div>

      <div v-else>
        <p class="text-muted" style="margin-bottom: 16px;">
          Sélectionne les colonnes à inclure dans l'export. Toutes les colonnes sont cochées par défaut.
        </p>

        <div class="export-columns-presets">
          <span class="export-columns-presets__label">Mes presets&nbsp;:</span>
          <el-select
            v-model="selectedPresetId"
            placeholder="Charger un preset…"
            size="small"
            clearable
            :disabled="presets.length === 0"
            style="width: 240px;"
            @change="loadPreset">
            <el-option
              v-for="p in presets"
              :key="p.id"
              :label="p.name"
              :value="p.id" />
          </el-select>
          <el-button
            size="mini"
            type="danger"
            plain
            icon="el-icon-delete"
            :disabled="!selectedPresetId"
            @click="deletePreset">
            Supprimer
          </el-button>
        </div>

        <div style="margin-bottom: 12px;">
          <el-button size="mini" @click="checkAll">Tout cocher</el-button>
          <el-button size="mini" @click="uncheckAll">Tout décocher</el-button>
          <span class="text-muted" style="margin-left: 12px; font-size: 0.85em;">
            {{ selected.length }} / {{ totalColumns }} colonnes sélectionnées
          </span>
        </div>

        <div class="export-columns-groups">
          <div v-for="group in groups" :key="group.title" class="export-columns-group">
            <el-checkbox
              :indeterminate="isGroupIndeterminate(group)"
              :value="isGroupAllChecked(group)"
              @change="toggleGroup(group, $event)">
              <strong>{{ group.title }}</strong>
            </el-checkbox>
            <div style="margin-left: 24px; margin-top: 6px;">
              <el-checkbox
                v-for="column in group.columns"
                :key="column"
                :label="column"
                v-model="selectedMap[column]"
                :disabled="isRequired(column)"
                @change="syncSelectedFromMap"
                style="display: block; margin-left: 0;">
                {{ column }}
                <span v-if="isRequired(column)" class="text-muted" style="font-size: 0.85em;">(obligatoire)</span>
              </el-checkbox>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center" style="padding: 20px 0;">
      <div v-if="!finished && !errored">
        <i class="el-icon-loading" style="font-size: 48px; color: #409eff;"></i>
        <p class="mt-3" style="margin-top: 20px;">Génération du fichier {{ label }} en cours…</p>
        <p class="text-muted" style="font-size: 0.85em;">Le téléchargement démarrera automatiquement à la fin.</p>
      </div>

      <div v-else-if="finished">
        <i class="el-icon-success" style="font-size: 48px; color: #67c23a;"></i>
        <p class="mt-3" style="margin-top: 20px;">Téléchargement démarré.</p>
      </div>

      <div v-else-if="errored">
        <i class="el-icon-error" style="font-size: 48px; color: #f56c6c;"></i>
        <p class="mt-3" style="margin-top: 20px;">{{ errorMessage || 'Une erreur est survenue lors de la génération du fichier.' }}</p>
      </div>
    </div>

    <span slot="footer">
      <template v-if="step === 'select' && !loadingColumns">
        <el-button @click="visible = false">Annuler</el-button>
        <el-button
          icon="el-icon-star-off"
          :disabled="selected.length === 0 || savingPreset"
          :loading="savingPreset"
          @click="openSavePresetPrompt">
          Sauvegarder cette sélection
        </el-button>
        <el-button
          type="primary"
          :disabled="selected.length === 0 || submitting"
          :loading="submitting"
          @click="submit">
          Démarrer l'export
        </el-button>
      </template>
      <template v-else-if="finished || errored">
        <el-button @click="visible = false">Fermer</el-button>
      </template>
    </span>

    <el-dialog
      :visible.sync="savePresetVisible"
      title="Sauvegarder le preset"
      width="420px"
      append-to-body>
      <el-form @submit.native.prevent="confirmSavePreset">
        <el-form-item label="Nom du preset">
          <el-input
            v-model="newPresetName"
            ref="newPresetInput"
            placeholder="Ex: Export nutrition uniquement"
            maxlength="100"
            show-word-limit />
        </el-form-item>
        <p v-if="presetNameExists" class="text-muted" style="margin-top: -8px; font-size: 0.85em; color: #e6a23c;">
          Un preset porte déjà ce nom — il sera écrasé.
        </p>
      </el-form>
      <span slot="footer">
        <el-button @click="savePresetVisible = false">Annuler</el-button>
        <el-button
          type="primary"
          :disabled="!newPresetName.trim() || savingPreset"
          :loading="savingPreset"
          @click="confirmSavePreset">
          Sauvegarder
        </el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
/**
 * Modal générique de sélection de colonnes pour un export, avec presets par utilisateur.
 * - mode "async" (défaut, export Ecobalyse recettes) : lance un job puis interroge une URL de progression
 *   et redirige vers le fichier une fois prêt.
 * - mode "download" (export ingrédients) : redirige directement vers l'URL d'export (téléchargement synchrone).
 */
export default {
  props: {
    label: { type: String, default: 'Ecobalyse' },
    columnsUrl: { type: String, default: '/pro/recipes/export-ecobalyse/columns' },
    presetsUrl: { type: String, default: '/pro/recipes/export-ecobalyse/presets' },
    submitUrl: { type: String, default: '/pro/recipes' },
    exportValue: { type: [String, Number], default: 4 },
    mode: { type: String, default: 'async' }, // 'async' | 'download'
    progressUrl: { type: String, default: '/pro/recipes/export-ecobalyse/progress/' },
    method: { type: String, default: 'get' }, // 'get' | 'post' (post : filtres envoyés dans le corps, sans limite d'URL)
  },
  data() {
    return {
      visible: false,
      step: 'select', // 'select' | 'loading'
      filters: {},
      groups: [],
      required: [],
      selected: [],
      selectedMap: {},
      loadingColumns: false,
      submitting: false,
      exportId: null,
      status: 'waiting',
      errorMessage: null,
      pollTimer: null,
      presets: [],
      selectedPresetId: null,
      savePresetVisible: false,
      newPresetName: '',
      savingPreset: false,
    };
  },
  computed: {
    finished() {
      return this.status === 'finish';
    },
    errored() {
      return this.status === 'error';
    },
    totalColumns() {
      return this.groups.reduce((acc, g) => acc + g.columns.length, 0);
    },
    title() {
      if (this.finished) return 'Export terminé';
      if (this.errored) return 'Erreur';
      if (this.step === 'select') return 'Export ' + this.label + ' — choisir les colonnes';
      return 'Export ' + this.label + ' en cours';
    },
    presetNameExists() {
      const n = this.newPresetName.trim().toLowerCase();
      if (!n) return false;
      return this.presets.some((p) => p.name.toLowerCase() === n);
    },
  },
  methods: {
    open(filters) {
      this.filters = filters || {};
      this.step = 'select';
      this.status = 'waiting';
      this.errorMessage = null;
      this.exportId = null;
      this.groups = [];
      this.required = [];
      this.selected = [];
      this.selectedMap = {};
      this.submitting = false;
      this.selectedPresetId = null;
      this.visible = true;
      this.fetchColumns();
      this.fetchPresets();
    },
    async fetchColumns() {
      this.loadingColumns = true;
      try {
        const { data } = await axios.get(this.columnsUrl);
        this.groups = data.groups || [];
        this.required = data.required || [];
        const map = {};
        const all = [];
        this.groups.forEach((g) => {
          g.columns.forEach((c) => {
            map[c] = true;
            all.push(c);
          });
        });
        this.selectedMap = map;
        this.selected = all;
      } catch (e) {
        this.step = 'loading';
        this.status = 'error';
        this.errorMessage = 'Impossible de charger la liste des colonnes.';
      } finally {
        this.loadingColumns = false;
      }
    },
    async fetchPresets() {
      try {
        const { data } = await axios.get(this.presetsUrl);
        this.presets = data.presets || [];
      } catch (e) {
        this.presets = [];
      }
    },
    loadPreset(presetId) {
      if (!presetId) return;
      const preset = this.presets.find((p) => p.id === presetId);
      if (!preset) return;
      const wanted = new Set(preset.columns);
      const map = {};
      this.groups.forEach((g) => {
        g.columns.forEach((c) => {
          map[c] = wanted.has(c) || this.isRequired(c);
        });
      });
      this.selectedMap = map;
      this.syncSelectedFromMap();
      this.$message({ type: 'success', message: 'Preset « ' + preset.name + ' » chargé.' });
    },
    openSavePresetPrompt() {
      const current = this.selectedPresetId
        ? this.presets.find((p) => p.id === this.selectedPresetId)
        : null;
      this.newPresetName = current ? current.name : '';
      this.savePresetVisible = true;
      this.$nextTick(() => {
        if (this.$refs.newPresetInput && this.$refs.newPresetInput.focus) {
          this.$refs.newPresetInput.focus();
        }
      });
    },
    async confirmSavePreset() {
      const name = this.newPresetName.trim();
      if (!name) return;
      this.savingPreset = true;
      try {
        const { data } = await axios.post(this.presetsUrl, {
          name: name,
          columns: this.selected,
        });
        this.presets = data.presets || [];
        this.selectedPresetId = data.preset ? data.preset.id : null;
        this.savePresetVisible = false;
        this.$message({ type: 'success', message: 'Preset sauvegardé.' });
      } catch (e) {
        this.$message({ type: 'error', message: 'Erreur lors de la sauvegarde du preset.' });
      } finally {
        this.savingPreset = false;
      }
    },
    async deletePreset() {
      if (!this.selectedPresetId) return;
      const preset = this.presets.find((p) => p.id === this.selectedPresetId);
      if (!preset) return;
      try {
        await this.$confirm(
          'Supprimer le preset « ' + preset.name + ' » ?',
          'Confirmation',
          { confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler', type: 'warning' }
        );
      } catch (e) {
        return;
      }
      try {
        const { data } = await axios.delete(this.presetsUrl + '/' + encodeURIComponent(this.selectedPresetId));
        this.presets = data.presets || [];
        this.selectedPresetId = null;
        this.$message({ type: 'success', message: 'Preset supprimé.' });
      } catch (e) {
        this.$message({ type: 'error', message: 'Erreur lors de la suppression du preset.' });
      }
    },
    isGroupAllChecked(group) {
      return group.columns.every((c) => this.selectedMap[c]);
    },
    isGroupIndeterminate(group) {
      const checked = group.columns.filter((c) => this.selectedMap[c]).length;
      return checked > 0 && checked < group.columns.length;
    },
    isRequired(column) {
      return this.required.indexOf(column) !== -1;
    },
    toggleGroup(group, value) {
      group.columns.forEach((c) => {
        this.$set(this.selectedMap, c, !!value || this.isRequired(c));
      });
      this.syncSelectedFromMap();
    },
    syncSelectedFromMap() {
      this.selected = Object.keys(this.selectedMap).filter((k) => this.selectedMap[k]);
    },
    checkAll() {
      this.groups.forEach((g) => this.toggleGroup(g, true));
    },
    uncheckAll() {
      this.groups.forEach((g) => this.toggleGroup(g, false));
    },
    async submit() {
      this.submitting = true;
      const params = Object.assign({}, this.filters, {
        export: this.exportValue,
        selected_columns: this.selected,
      });

      if (this.mode === 'download') {
        // Téléchargement synchrone : le navigateur reçoit directement le fichier.
        window.location.href = this.submitUrl + '?' + this.buildQuery(params);
        this.submitting = false;
        this.visible = false;
        return;
      }

      try {
        const { data } = this.method === 'post'
          ? await axios.post(this.submitUrl, params, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          : await axios.get(this.submitUrl, { params, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        this.exportId = data.export_id;
        this.step = 'loading';
        this.status = 'waiting';
        this.fetchProgress();
        this.pollTimer = setInterval(this.fetchProgress, 2000);
      } catch (e) {
        this.step = 'loading';
        this.status = 'error';
        this.errorMessage = 'Erreur lors du lancement de l\'export.';
      } finally {
        this.submitting = false;
      }
    },
    async fetchProgress() {
      try {
        const { data } = await axios.get(this.progressUrl + this.exportId);
        this.status = data.status;
        if (data.status === 'finish' && data.download_url) {
          this.stopPolling();
          window.location.href = data.download_url;
        } else if (data.status === 'error') {
          this.stopPolling();
        }
      } catch (e) {
        this.status = 'error';
        this.stopPolling();
      }
    },
    buildQuery(params) {
      const parts = [];
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value)) {
          value.forEach((v) => parts.push(encodeURIComponent(key + '[]') + '=' + encodeURIComponent(v)));
        } else {
          parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
        }
      });
      return parts.join('&');
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
};
</script>

<style scoped>
.export-columns-presets {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #f5f7fa;
  border-radius: 4px;
}
.export-columns-presets__label {
  font-size: 0.9em;
  color: #606266;
  white-space: nowrap;
}
.export-columns-groups {
  max-height: 450px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 12px;
}
.export-columns-group {
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f2f6fc;
}
.export-columns-group:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}
</style>
